import { Field, Float, ID, InputType, Int, ObjectType } from 'type-graphql'
import { modelOptions, Prop } from '@typegoose/typegoose'
import { BaseEntity } from '../../base/BaseEntity'
import { generateEntityId } from '../../generateEntityId'
import { CouponDiscountTypeEnum } from './CouponDiscountTypeEnum'
import { CouponRedemption } from './CouponRedemption'

/**
 * A self-managed promo code that discounts a purchase's charged price before a payment
 * gateway session is created. `code` is the redemption key (uppercased at write time by
 * the owning repository/action — see `backend-repository-style-guide.md`); the discount
 * itself never touches the credits granted, only the amount charged.
 */
@ObjectType()
@InputType('CouponInput')
@modelOptions({ schemaOptions: { timestamps: true, collection: 'coupons' } })
export class Coupon extends BaseEntity {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('cpn') })
  declare _id: string

  /** Redemption key, uppercased. Unique — see CouponSchema. */
  @Field(() => String)
  @Prop({ type: String, required: true })
  declare code: string

  @Field(() => CouponDiscountTypeEnum)
  @Prop({ type: String, enum: CouponDiscountTypeEnum, required: true })
  discountType: CouponDiscountTypeEnum

  /** Percentage (0-100) or a flat amount, depending on `discountType`. */
  @Field(() => Float)
  @Prop({ type: Number, required: true })
  discountValue: number

  /** Total redemptions allowed across all owners — unset means unlimited. */
  @Field(() => Int, { nullable: true })
  @Prop({ type: Number })
  maxRedemptions?: number

  @Field(() => Int)
  @Prop({ type: Number, required: true, default: 0 })
  redemptionCount: number

  /** Max redemptions allowed per owner. */
  @Field(() => Int, { nullable: true })
  @Prop({ type: Number, default: 1 })
  perUserLimit?: number

  /** Minimum purchase amount required for this coupon to apply, in the purchase's own currency. */
  @Field(() => Float, { nullable: true })
  @Prop({ type: Number })
  minPurchaseAmount?: number

  @Field(() => Date, { nullable: true })
  @Prop({ type: Date })
  validFrom?: Date

  @Field(() => Date, { nullable: true })
  @Prop({ type: Date })
  validUntil?: Date

  @Field(() => Boolean)
  @Prop({ type: Boolean, required: true, default: true })
  active: boolean

  @Field(() => [CouponRedemption])
  redemptions: ReturnType<() => CouponRedemption[]>
}
